import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((res) => {
      setUser(res.data);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data));
    }).catch(() => {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(null);
    }).finally(() => setLoading(false));
    // Runs once on mount; all references (api, setUser, setLoading, localStorage) are
    // stable module/hook identities — no reactive deps to track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_STORAGE_KEY, res.data.token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const signup = async (payload) => {
    const res = await api.post("/auth/signup", payload);
    localStorage.setItem(TOKEN_STORAGE_KEY, res.data.token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
    window.location.href = "/";
  };

  // Memoize the context value so consumers don't re-render on every AuthProvider render
  // caused by unrelated state changes.
  const value = useMemo(
    () => ({ user, setUser, login, signup, logout, loading }),
    [user, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
